import React, { useEffect, useState, useRef } from 'react';
import Button from 'components/library/Button';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';

import BrandIcon from 'components/global/Icon/Brand';
import Icon from 'components/global/Icon/Primary';
import {
  MicrobioSection,
  MicrobiomeContainer,
  BiomeMedia,
  MicrobiomeHeader,
  CarouselContainer,
  ControlsContainer,
  Controls,
  FullscreenButton,
  ProgressWrapper,
  ProgressBuffer,
  ProgressBar,
  ProgressButton,
  ChapterButton,
  ChapterConnector,
  FlexContainer,
  MicrobiomeIntro,
  MicrobiomeIntroBook,
  MicrobiomeIntroContent,
  MicrobiomeIntroImage,
  MicrobiomeIntroStill,
  MicrobiomeFooter,
  MicrobiomeFooterLabel,
  MicrobiomeFooterTitle,
  PanelTitle,
  BookContainer,
  DiscoverButton,
  DiscoverButtonSmall,
  PlayCircle,
} from 'components/library/MicroBiome/micro-biome.styled';

import {
  TypeBody,
  TypeHeadingSecondary,
  TypeCaption,
} from 'components/library/Typography';

import 'swiper/css';
import 'swiper/css/navigation';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { Swiper as SwiperType } from 'swiper/types';
import {
  AnimatePresence,
  motion,
  Variants,
  useAnimation,
  useReducedMotion,
} from 'motion/react';
import { ease } from 'styled/tools/motion';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import { pxToEm } from 'styled/tools/units';
import {
  microbiomeImageSlide,
  microbiomeIntroStill,
  microbiomeVideo,
} from 'components/home/content/micro-biome.content';
import MuxPlayer from 'components/reusable/MuxPlayer/mux-player';

interface MicrobiomeProps {
  chapters: any[];
}

interface MicrobiomePlayerProps {
  chapters: any[];
  onDiscoverClick: (bool: boolean) => void;
}

interface MicrobiomeIntroProps {
  onDiscoverClick: (bool: boolean) => void;
}

const duration = 0.6;

const microbiomeIntroVariantsMobile: Variants = {
  initial: {
    opacity: 0,
    transition: {
      ease: ease as any,
      duration: 0.25,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      ease: ease as any,
      duration: 0.25,
    },
  },
  visible: {
    background: 'rgba(252, 252, 247, 1)',
    opacity: 1,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: pxToEm(46),
    transition: {
      ease: ease as any,
      duration,
    },
  },
};

const microbiomeIntroVariantsDesktop: Variants = {
  initial: {
    opacity: 0,
    transition: {
      ease: ease as any,
      duration: 0.25,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      ease: ease as any,
      duration: 0.25,
    },
  },
  visible: {
    background: 'rgba(252, 252, 247, 1)',
    opacity: 1,
    padding: '24px',
    transition: {
      ease: ease as any,
      duration,
    },
  },
};

const controlsVariants = {
  initial: {
    opacity: 0,
    scale: 0.8,
    transition: {
      ease,
      duration,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: {
      ease,
      duration,
    },
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      ease,
      duration,
    },
  },
};

const subjectVariants = {
  initial: {
    opacity: 0,
    transition: {
      delay: 0.5,
      ease,
      duration,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      delay: 0.5,
      ease,
      duration,
    },
  },
  visible: {
    opacity: 1,
    transition: {
      delay: 0.5,
      ease,
      duration,
    },
  },
};

const frameVariants = {
  initial: {
    background: 'rgba(238, 238, 233, 1)',
    height: 'auto',
    width: 'calc(6 * var(--grid-col-width) + 5 * 20px)',
    opacity: 1,
    transition: {
      ease,
      duration,
    },
  },
  exit: {
    background: 'rgba(252, 252, 247, 1)',
    height: '760px',
    width: '100%',
    opacity: 0,
    transition: {
      ease,
      duration,
      background: {
        delay: 0.3,
      },
      opacity: {
        delay: 0.6,
      },
    },
  },
};

// TODO: This might be needed to add a transition for the MuxPlayer
// const mediaVariants = {
//   initial: {
//     opacity: 1,
//     transition: {
//       ease,
//       duration,
//     },
//   },
//   exit: {
//     opacity: 0,
//     transition: {
//       ease,
//       duration,
//     },
//   },
// };

const headerVariants = {
  initial: {
    y: -100,
  },
  visible: {
    y: 0,
    transition: {
      delay: 0.3,
      ease,
      duration: 1,
    },
  },
};

const MicrobiomeIntroComponent: React.FC<MicrobiomeIntroProps> = ({
  onDiscoverClick,
}) => {
  const isSmall = useClientSideMediaQuery(960);
  const controls = useAnimation();
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const shouldReduceMotion = useReducedMotion();
  const [showPreview, setShowPreview] = useState(false);

  // The server can't read the motion setting, so CSS shows the still until this runs.
  useEffect(() => {
    setShowPreview(!shouldReduceMotion);
  }, [shouldReduceMotion]);

  const handleClick = () => {
    buttonEventHandler('Discover More', 'Microbiome');
    if (!isSmall) {
      controls.start('exit');
    }
  };

  const previewPlaybackId =
    process.env.NEXT_PUBLIC_STAGING === 'true'
      ? isSmall
        ? 'Omc7U9RpswLm1yj02h66ql8IGiaNtOH1r3Yy2Qtb802cw' // mobile dev
        : 'gkS50100SlMVrgeymIcKnHtJX4l2LTmF4dhhEC02rs7e500' // desktop dev
      : isSmall
        ? 'f93ezdDcsqOn02W018Of4DyqRyAk01Zcc800c900Td5NTGEc' // mobile prod
        : '87tnV011w6GkwNzl7dxntQSNhpcVSJNgSQaqlj3iLTK00'; // desktop prod

  return (
    <MicrobioSection>
      <MicrobiomeIntro>
        {isSmall ? (
          <span style={{ justifySelf: 'start' }}>
            <BrandIcon name='book' size='sm' />
          </span>
        ) : (
          <>
            <MicrobiomeIntroBook>
              Seed【 <BrandIcon name='book' size='sm' /> 】
            </MicrobiomeIntroBook>
          </>
        )}
        <MicrobiomeIntroContent>
          <TypeHeadingSecondary as='h2' color='primary'>
            You are more than human.
          </TypeHeadingSecondary>
          <TypeBody color='primary'>
            Your body isn't yours alone—it's home to 38 trillion microbes that
            power your digestion, immunity and more. Take a few minutes to learn
            how their health impacts your health—and how to maximize both.
          </TypeBody>
          {!isSmall ? (
            <DiscoverButton
              animated={false}
              onClick={() => {
                handleClick();
                setTimeout(() => {
                  onDiscoverClick(true);
                }, 600);
              }}
            >
              Discover{' '}
              <PlayCircle>
                <Icon name='play' size='md' />
              </PlayCircle>
            </DiscoverButton>
          ) : null}
        </MicrobiomeIntroContent>
        <MicrobiomeFooter>
          <MicrobiomeFooterLabel>SCIENCE /</MicrobiomeFooterLabel>
          <MicrobiomeFooterTitle>Microbiome 101</MicrobiomeFooterTitle>
        </MicrobiomeFooter>
        <MicrobiomeIntroImage
          animate={controls}
          initial='initial'
          variants={frameVariants}
        >
          <MicrobiomeIntroStill
            src={microbiomeIntroStill.src}
            alt=''
            loading='lazy'
          />
          {showPreview && (
            <MuxPlayer
              wrapperPosition='relative'
              playbackId={previewPlaybackId}
              style={{
                '--controls': 'none',
                '--media-object-fit': 'cover',
                aspectRatio: '1',
              }}
              muted
              autoPlay
              loop
              deferUntilNearView
              // Full source widths: at 2x DPR the slot needs more than either source has.
              posterWidth={isSmall ? 606 : 1920}
              videoRefs={videoRefs}
              index={0} // One video so index is 0 - We ~could~ modify the MuxPlayerAutoPause component to handle a single video without an array.
            />
          )}
          {isSmall && (
            <DiscoverButtonSmall
              animated={false}
              onClick={() => {
                handleClick();
                setTimeout(() => {
                  onDiscoverClick(true);
                }, 600);
              }}
            >
              Discover{' '}
              <PlayCircle>
                <Icon name='play' size='md' />
              </PlayCircle>
            </DiscoverButtonSmall>
          )}
        </MicrobiomeIntroImage>
      </MicrobiomeIntro>
    </MicrobioSection>
  );
};

const MicrobiomePlayer: React.FC<MicrobiomePlayerProps> = ({
  chapters,
  onDiscoverClick,
}) => {
  const swiperRef = useRef<SwiperType>(null);
  const isSmall = useClientSideMediaQuery(960);
  const containerRef = useRef<HTMLDivElement>(null);

  const [chapterNumber, setChapterNumber] = useState<number>(1);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [activeIndex, setActiveIndex] = useState(0);
  const progressRef = useRef<HTMLDivElement>(null);
  const bufferRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<any[]>([]);
  const carouselContainerRef = useRef<HTMLDivElement>(null);

  const [isWaiting, setIsWaiting] = useState(false);

  const handleVideoEnded = (videoElement?: HTMLVideoElement) => {
    swiperRef.current?.slideNext(300, false);

    // Reset the video that just finished to the beginning
    if (videoElement) {
      videoElement.load();
    }

    setActiveIndex(chapters.length - 1);
    setChapterNumber(chapters[chapters.length - 1]?.chapter);

    if (isPlaying) {
      videoRefs.current[0]?.pause();
      setIsPlaying(false);
    }

    // Reset progress bar and elapsed time
    if (progressRef.current) {
      progressRef.current.style.transform = 'scaleX(0)';
    }
  };

  const handleChapterClick = (index: number) => {
    if (swiperRef.current) {
      buttonEventHandler(`Micronav navigate to index ${index}`, 'Microbiome');
      if (chapters[index].type === 'image') {
        videoRefs.current[0]?.pause();
        setIsPlaying(false);
        swiperRef.current?.slideToLoop(index, 300, false);
      }

      if (chapters[index].type === 'video') {
        swiperRef.current?.slideToLoop(0, 300, false);
        videoRefs.current[0].pause();
        if (videoRefs.current[0]) {
          videoRefs.current[0].currentTime = chapters[index].startTime;
        }
      }

      setActiveIndex(index);
      setChapterNumber(chapters[index].chapter ?? null);

      // Reset progress bar and elapsed time
      if (progressRef.current) {
        progressRef.current.style.transform = 'scaleX(0)';
      }

      setTimeout(() => {
        if (videoRefs.current[0] && chapters[index].type === 'video') {
          videoRefs.current[0]?.play();
          setIsPlaying(true);
        }
      }, 100);
    }
  };

  const handleMuted = () => {
    const muteString = isMuted ? 'Unmute' : 'Mute';
    buttonEventHandler(`${muteString} video`, 'Microbiome');
    setIsMuted(!isMuted);
  };

  const handleFullScreen = () => {
    if (!isFullscreen) {
      carouselContainerRef.current?.requestFullscreen();
      buttonEventHandler('Enter Fullscreen', 'Microbiome');
    } else {
      document.exitFullscreen();
      buttonEventHandler('Exit Fullscreen', 'Microbiome');
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleExit = () => {
    buttonEventHandler('Exit video player', 'Microbiome');
    onDiscoverClick(false);
    const header = document.querySelector('header') as HTMLElement;
    header.style.display = 'block';
  };

  const handlePlayPauseClick = () => {
    if (!isPlaying) {
      videoRefs.current[0]?.play();
      if (isWaiting) setIsWaiting(false);
      setIsPlaying(true);
      buttonEventHandler(`Play Video at index ${activeIndex}`, 'Microbiome');
    } else {
      videoRefs.current[0]?.pause();
      setIsPlaying(false);
      setIsWaiting(false);
      buttonEventHandler(`Pause Video at index ${activeIndex}`, 'Microbiome');
    }
  };

  useEffect(() => {
    let rafId: number;
    const updateProgress = () => {
      const element = videoRefs.current[0];
      if (element && progressRef.current) {
        let duration = chapters[activeIndex]?.duration;
        let startTime = chapters[activeIndex]?.startTime || 0;
        let currentTime = element.currentTime;
        let progress = 0;
        if (
          chapters[activeIndex]?.type === 'video' &&
          typeof duration === 'number' &&
          duration > 0
        ) {
          progress = (currentTime - startTime) / duration;
        } else if (element.duration > 0) {
          progress = currentTime / element.duration;
        }

        if (chapters[activeIndex]?.type === 'image') {
          progress = 0;
        }
        progressRef.current.style.transform = `scaleX(${Math.max(0, Math.min(1, progress))})`;
      }
      rafId = requestAnimationFrame(updateProgress);
    };
    updateProgress();
    return () => cancelAnimationFrame(rafId);
  }, [activeIndex, chapters]);

  const [caption, setCaption] = useState('');
  useEffect(() => {
    const videoEl = videoRefs.current[0];

    if (!videoEl) return;

    const track = Array.from(videoEl.textTracks).find((t) => {
      const kind = (t as TextTrack).kind;
      return kind === 'subtitles' || kind === 'captions';
    }) as TextTrack | undefined;

    if (!track) return;

    track.mode = 'hidden';

    const handleTimeUpdate = () => {
      const cue = track.activeCues?.[0] as VTTCue;
      setCaption(cue ? cue.text : '');

      // Update activeIndex based on currentTime and chapters' startTimes
      if (
        chapters &&
        chapters.length &&
        chapters[activeIndex]?.type === 'video'
      ) {
        const currentTime = videoEl.currentTime;
        let idx = 0;
        for (let i = 0; i < chapters.length; i++) {
          if (
            chapters[i].type === 'video' &&
            typeof chapters[i].startTime === 'number' &&
            currentTime >= chapters[i].startTime
          ) {
            idx = i;
          }
        }
        if (idx !== activeIndex) {
          setActiveIndex(idx);
          setChapterNumber(chapters[idx].chapter ?? null);
        }
      }
    };

    videoEl.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      videoEl.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [isPlaying, activeIndex]);

  // Scroll to center player on mobile when component mounts
  useEffect(() => {
    if (containerRef.current) {
      const header = document.querySelector('header') as HTMLElement;
      header.style.display = 'none';
      requestAnimationFrame(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      });
    }
  }, []); // Run once on mount

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    function trySetupAndPlay() {
      const videoEl = videoRefs.current[0];
      if (!videoEl) return;

      const track = Array.from(videoEl.textTracks).find((t) => {
        const kind = (t as TextTrack).kind;
        return kind === 'subtitles' || kind === 'captions';
      }) as TextTrack | undefined;

      if (track) {
        track.mode = 'hidden';
        videoEl.play();
        setIsPlaying(true);
        if (interval) clearInterval(interval);
      }
    }
    interval = setInterval(trySetupAndPlay, 100);
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  return (
    <MicrobiomeContainer ref={containerRef}>
      <CarouselContainer ref={carouselContainerRef}>
        <MicrobiomeHeader
          initial='initial'
          animate='visible'
          exit='exit'
          variants={headerVariants}
        >
          <BookContainer as='h2' color='inverse-primary'>
            <BrandIcon name='book' size='sm' />{' '}
            {chapterNumber ? `Chapter ${chapterNumber}` : ''}
          </BookContainer>
          <FlexContainer>
            {!isSmall ? (
              <FullscreenButton onClick={handleFullScreen}>
                <Icon name='fullscreen' size='md' aria-label='Fullscreen' />
              </FullscreenButton>
            ) : null}
            <FullscreenButton onClick={handleExit}>
              <Icon name='close' size='md' aria-label='Close' />
            </FullscreenButton>
          </FlexContainer>
        </MicrobiomeHeader>
        <Swiper
          modules={[Navigation]}
          slidesPerView={1}
          grabCursor={true}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
        >
          {[microbiomeVideo, microbiomeImageSlide].map(
            (video: any, index: number) => (
              <SwiperSlide key={index} onClick={handlePlayPauseClick}>
                {video.type === 'video' ? (
                  <MuxPlayer
                    data-microbiome-player
                    playbackId={
                      process.env.NEXT_PUBLIC_STAGING === 'true'
                        ? isSmall
                          ? video.mobileDevPlaybackId
                          : video.devPlaybackId
                        : isSmall
                          ? video.mobilePlaybackId
                          : video.playbackId
                    }
                    videoRefs={videoRefs}
                    lazy={false}
                    // @ts-ignore
                    style={{
                      '--controls': 'none',
                      '--media-object-fit': 'cover',
                      borderRadius: 'var(--radius-xl)',
                    }}
                    muted={isMuted}
                    onPause={() => setIsPlaying(false)}
                    isManuallyControlled={true}
                    index={index}
                    onEnded={(e) =>
                      handleVideoEnded(e.target as HTMLVideoElement)
                    }
                  />
                ) : (
                  <BiomeMedia as='img' src={video.src} alt={video.title} />
                )}
              </SwiperSlide>
            ),
          )}
        </Swiper>
        <ControlsContainer layout>
          <AnimatePresence key={chapters[activeIndex].title}>
            {chapters[activeIndex].type === 'video' ? (
              <motion.div
                layout='position'
                variants={subjectVariants}
                initial='initial'
                animate='visible'
                exit='exit'
              >
                <TypeCaption color='primary'>{caption}</TypeCaption>
              </motion.div>
            ) : (
              <motion.div
                layout='position'
                variants={subjectVariants}
                initial='initial'
                animate='visible'
                exit='exit'
              >
                <PanelTitle as='h3' color='primary'>
                  {chapters[activeIndex].heading}
                </PanelTitle>
                <TypeBody color='primary'>
                  {chapters[activeIndex].description}
                </TypeBody>
              </motion.div>
            )}
          </AnimatePresence>
          <Controls
            layout
            style={{ borderRadius: '16px' }}
            transition={{
              default: { ease: 'linear' },
            }}
          >
            <Button onClick={handleMuted} size='small' color='seedgreen'>
              {isMuted ? (
                <Icon name='volume-off' size='md' aria-label='Unmute' />
              ) : (
                <Icon name='volume-on' size='md' aria-label='Mute' />
              )}
            </Button>

            <AnimatePresence key='microbiome-controls-progress'>
              {[...chapters].map((item: any, index: number) => {
                const elements: React.ReactElement[] = [];

                // Add connector before this element (except for the first element)
                if (index > 0) {
                  //} && index !== activeIndex && index - 1 !== activeIndex) {
                  elements.push(
                    <ChapterConnector key={`connector-${index}`} />,
                  );
                }

                // Add the chapter button or progress wrapper
                if (index !== activeIndex) {
                  elements.push(
                    <ChapterButton
                      type='button'
                      key={`button-${index}`}
                      onClick={() => handleChapterClick(index)}
                      aria-label={item.title}
                    />,
                  );
                } else {
                  elements.push(
                    <ProgressWrapper
                      key={`progress-${index}`}
                      layout
                      initial='initial'
                      animate='visible'
                      exit='exit'
                      variants={controlsVariants}
                    >
                      <ProgressBuffer ref={bufferRef} />
                      <ProgressBar ref={progressRef} />
                      <ProgressButton
                        onClick={
                          chapters[activeIndex].type !== 'image'
                            ? handlePlayPauseClick
                            : null
                        }
                        layout
                      >
                        <motion.span
                          key={`${index}-${chapters[activeIndex].title}`}
                          layout='position'
                          variants={subjectVariants}
                          initial='initial'
                          animate='visible'
                          exit='exit'
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '13px',
                            letterSpacing: '-0.42px',
                          }}
                        >
                          {chapters[activeIndex].type !== 'image' &&
                            (isPlaying ? (
                              <Icon name='pause' size='md' />
                            ) : (
                              <Icon name='play' size='md' />
                            ))}
                          {chapters[activeIndex].title}
                        </motion.span>
                      </ProgressButton>
                    </ProgressWrapper>,
                  );
                }

                return (
                  <React.Fragment key={`fragment-${index}`}>
                    {elements}
                  </React.Fragment>
                );
              })}
            </AnimatePresence>
          </Controls>
        </ControlsContainer>
      </CarouselContainer>
    </MicrobiomeContainer>
  );
};

const Microbiome = ({ chapters }: MicrobiomeProps) => {
  const [showVideos, setShowVideos] = useState(false);
  const isSmall = useClientSideMediaQuery(960);

  const handleDiscoverClick = (bool: boolean) => {
    setShowVideos(bool);
  };

  return (
    <>
      {showVideos ? (
        <AnimatePresence key='microbiome-player'>
          <motion.div
            initial='initial'
            animate='visible'
            exit='exit'
            variants={
              isSmall
                ? microbiomeIntroVariantsMobile
                : microbiomeIntroVariantsDesktop
            }
          >
            <MicrobiomePlayer
              chapters={chapters}
              onDiscoverClick={handleDiscoverClick}
            />
          </motion.div>
        </AnimatePresence>
      ) : (
        <AnimatePresence key='microbiome-intro'>
          <MicrobiomeIntroComponent onDiscoverClick={handleDiscoverClick} />
        </AnimatePresence>
      )}
    </>
  );
};

export default Microbiome;
